import { ComponentProps, FC, useMemo } from 'react'

import { useFragment } from '@apollo/client'
import { theme } from '@factorialco/deprecated-design-system'
import { F0Link } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import {
  LeaveApprovalRecommendationFragment,
  TimeoffLeaveApprovableFragmentDoc,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { getUIDuration, i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import { EmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import NoteField from 'modules/inbox_tasks/components/NoteField'
import LeaveApprovalRecommendation from 'modules/timeoff/components/LeaveApprovalRecommendation'
import { useLeaveRecommendationFeatureFlag } from 'modules/timeoff/hooks/useLeaveRecommendationFeatureFlag'

import { getTimeoffDefaultsLabel } from './helpers'

type TimeoffLeaveTaskContentProps = {
  id: number
  requesterContent: EmployeeTableViewRow
  requesterEmployeeId?: number
}

const TimeoffLeaveTaskContent: FC<TimeoffLeaveTaskContentProps> = ({
  id,
  requesterEmployeeId,
  requesterContent,
}) => {
  const { navigation } = useNavigation()

  const { data } = useFragment({
    fragment: TimeoffLeaveApprovableFragmentDoc,
    fragmentName: 'TimeoffLeaveApprovable',
    from: {
      __typename: 'TimeoffLeave',
      id,
    },
    optimistic: false,
  })

  const { loading: ffLoading, enabled: isRecommendationEnabled } =
    useLeaveRecommendationFeatureFlag()

  const typeContent = useMemo(() => {
    const leaveType = data?.leaveType

    if (!leaveType) {
      return null
    }

    const text =
      leaveType?.identifier === 'custom'
        ? leaveType.name ?? ''
        : getTimeoffDefaultsLabel(leaveType?.identifier ?? 'generic')

    if (!text) {
      return null
    }

    const color = leaveType.color ? `#${leaveType.color}` : theme.colors.grey400

    return {
      text,
      customColor: color,
      type: 'dot-tag' as const,
    }
  }, [data?.leaveType])

  if (!data) {
    return null
  }

  const startOn = data.startOn ? stringToDate(data.startOn, 'backend_datetime') : undefined
  const endOn = data.finishOn ? stringToDate(data.finishOn, 'backend_datetime') : undefined

  const displayStartOn = startOn ? i18nDateToString(startOn, 'ui_dmy_long') : ''
  const displayEndOn = endOn ? i18nDateToString(endOn, 'ui_dmy_long') : ''

  const duration =
    startOn && endOn
      ? getUIDuration({
          date: endOn.clone().add(1, 'day'),
          fromDate: startOn,
        })
      : i18n.t('dashboard.new_upcoming_absences.no_end_date')

  const period = displayStartOn
    ? displayEndOn && displayEndOn !== displayStartOn
      ? `${displayStartOn} - ${displayEndOn}`
      : displayStartOn
    : ''

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    requesterContent,
    typeContent && {
      title: i18n.t('tasks.approvals.timeoff_leave.fields.type'),
      content: typeContent,
    },
    period && {
      title: i18n.t('tasks.approvals.timeoff_leave.fields.period'),
      content: {
        text: period,
        type: 'item' as const,
      },
    },
    {
      title: i18n.t('tasks.approvals.timeoff_leave.fields.duration'),
      content: {
        text: duration,
        type: 'item' as const,
      },
    },
  ].filter(Boolean)

  const employeeName = requesterContent?.content
    ? getEmployeeDisplayName({
        fullName: requesterContent.content.firstName,
        lastName: requesterContent.content.lastName,
      })
    : ''

  const isLeaveApprovalRecommendation = (
    obj: unknown
  ): obj is LeaveApprovalRecommendationFragment => {
    return Boolean(obj && typeof obj === 'object' && obj !== null && 'action' in obj)
  }

  const recommendation =
    data.approvalRecommendation && isLeaveApprovalRecommendation(data.approvalRecommendation)
      ? data.approvalRecommendation
      : null

  const showRecommendation = !ffLoading && isRecommendationEnabled

  return (
    <>
      {showRecommendation && recommendation && requesterEmployeeId && (
        <LeaveApprovalRecommendation
          employeeName={employeeName}
          employeeId={requesterEmployeeId}
          recommendation={recommendation}
          workable={data.leaveType?.workable}
        />
      )}
      <DetailsItemsList details={details} tableView />
      {requesterEmployeeId && (
        <F0Link
          href={navigation.employees.id.timeOff.leavesEditLeaveId.fullPath({
            id: requesterEmployeeId,
            leave_id: id,
          })}
        >
          {i18n.t('tasks.view_more_details')}
        </F0Link>
      )}
      {!!data.leaveDescription && (
        <NoteField
          label={i18n.t('tasks.approvals.timeoff_leave.fields.note')}
          content={data.leaveDescription}
        />
      )}
    </>
  )
}

export default TimeoffLeaveTaskContent

export * from './metadata'
